// Возврат после входа (Ф0, docs/product/engineering-plan.md). Гость открыл страницу приложения —
// ссылку из письма (/repeat?module=…), ссылку на модуль — и попал на вход. AppGate запоминает адрес,
// а после входа или знакомства RequireStage и OnboardingGate ведут туда, а не на главную.
//
// sessionStorage, а не localStorage: адрес живёт в этой вкладке — переживает переход в Яндекс ID и
// обратно, но не всплывает через неделю в другой вкладке. Срок — 30 минут.
//
// Модуль без импортов приложения: его проверяет tests/return-to.test.ts под Node.

export const RETURN_TO_KEY = 'lx.returnTo';
export const RETURN_TO_TTL_MS = 30 * 60 * 1000;
const MAX_LENGTH = 512;

/** Вход, OAuth и знакомство сами ведут дальше: возврат туда замкнул бы круг. Маршруты React Router
 *  не различают регистр, поэтому и проверка тоже. */
const EXCLUDED_PATH = /^\/(?:auth|oauth|onboarding)(?:\/|$)/i;

/** Секреты (`?token=` писем, `?ticket=` уведомлений) и клики рекламы в адрес возврата не попадают:
 *  повторный yclid в адресе задваивает клик Директа (решение B), а сами клики уже в атрибуции. */
const DROPPED_PARAMS = new Set(['token', 'ticket', 'yclid', 'gclid']);

/** Служебное начало адреса: превращает путь в URL, чтобы нормализовать его штатным разбором. */
const BASE = 'https://return-to.invalid';

export type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function sessionStore(): KeyValueStore | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null; // хранилище заблокировано настройками приватности
  }
}

/**
 * Безопасный адрес возврата — путь этого приложения от корня («/units/1?x=1») — или null.
 * Отсекает чужие адреса («//host», «/\host», «https://…»), управляющие символы, страницы входа
 * и знакомства, адреса длиннее 512 символов; убирает секреты и клики рекламы из query.
 */
export function safeReturnPath(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > MAX_LENGTH) return null;
  // «/\host» браузер читает как «//host»; переводы строк и табы разбор URL молча выбрасывает.
  if (!value.startsWith('/') || value.startsWith('//') || /[\\\u0000-\u001f\u007f]/.test(value)) return null;
  let url: URL;
  try {
    url = new URL(value, BASE);
  } catch {
    return null;
  }
  // После разбора «/a/../auth» уже «/auth»: исключения проверяем по нормализованному пути.
  if (url.origin !== BASE || EXCLUDED_PATH.test(url.pathname)) return null;
  const params = new URLSearchParams(url.search);
  [...params.keys()].forEach((key) => {
    if (DROPPED_PARAMS.has(key.toLowerCase())) params.delete(key);
  });
  const query = params.toString();
  const path = url.pathname + (query ? `?${query}` : '');
  return path.length <= MAX_LENGTH ? path : null;
}

/** Запомнить, куда шёл гость. Главная не запоминается: туда и так ведёт вход. */
export function rememberReturnTo(path: string, now = Date.now(), store = sessionStore()): void {
  const safe = safeReturnPath(path);
  if (!safe || safe === '/' || safe.startsWith('/?')) return;
  try {
    store?.setItem(RETURN_TO_KEY, JSON.stringify({ path: safe, at: now }));
  } catch {
    // хранилище переполнено или заблокировано — после входа просто откроется главная
  }
}

/** Адрес возврата, если он есть и не старше 30 минут; читается один раз и сразу стирается. */
export function consumeReturnTo(now = Date.now(), store = sessionStore()): string | null {
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(RETURN_TO_KEY);
    store.removeItem(RETURN_TO_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as { path?: unknown; at?: unknown };
    const age = now - Number(stored.at);
    if (!(age >= 0 && age <= RETURN_TO_TTL_MS)) return null;
    // Хранилище могли подменить: адрес проверяется ещё раз.
    return safeReturnPath(stored.path);
  } catch {
    return null;
  }
}

/** Адрес возврата без стирания — чтобы знакомство поняло, что гость шёл в игру (решение владельца 2026-10-28). */
export function peekReturnTo(now = Date.now(), store = sessionStore()): string | null {
  if (!store) return null;
  try {
    const raw = store.getItem(RETURN_TO_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as { path?: unknown; at?: unknown };
    const age = now - Number(stored.at);
    return age >= 0 && age <= RETURN_TO_TTL_MS ? safeReturnPath(stored.path) : null;
  } catch {
    return null;
  }
}
